/**
 * Project Atlas — client entry (P0 vertical slice).
 *
 * Boots the EngineX App (Core + Render are built in) and registers the
 * Atlas-specific IModules. Flow:
 *   connect → resolve passport (kiosk or existing) → enter the Commons →
 *   spawn a networked avatar (prediction + reconciliation + AOI replication) →
 *   open the personal world (Tab) → place an object (E) that persists.
 *
 * Physics and XR modules join in later milestones; P0 movement is
 * server-authoritative with client prediction and needs no rigidbodies.
 */

import { App } from 'enginex/core/App.js';
import { installGpuDestroyShield, installDrawGuard } from './lib/gpu_destroy_shield.js';
import { installTerrainKernelFromUrl, currentTerrainKernel, optedOut as wasmKernelsOptedOut } from './lib/terrain_kernel.js';
import { preloadHydrologyKernel, setHydrologyKernel, activeHydrologyKernel, hydrologyKernelActive } from 'enginex/terrain/HydrologyKernel.js';
import { thermalErode } from 'enginex/terrain/TerrainErosion.js';
import { analyzeHydrology } from 'enginex/terrain/TerrainHydrology.js';
import { terrainHeight, setTerrainKernel, terrainKernelActive } from '../../shared/terrain.js';
import { CloudModule } from 'enginex/render/clouds';
import { isMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { atlas } from './state.js';
import { bus } from './bus.js';
import type { IModule } from 'enginex';
import { uSunDir, setDebugTod } from './modules/SkyModule.js';
import { AtlasIdentityModule } from './modules/AtlasIdentityModule.js';
import { AtlasNetworkModule } from './modules/AtlasNetworkModule.js';
import { AtlasWorldModule } from './modules/AtlasWorldModule.js';
import { AssetModule } from './modules/AssetModule.js';
import { accumulationHandles } from './lib/wetness.js';
import { PhysicsModule } from './modules/PhysicsModule.js';
import { RenderCullingModule } from './modules/RenderCullingModule.js';
import { LightingModule } from './modules/LightingModule.js';
import { SkyModule } from './modules/SkyModule.js';
import { PostModule } from './modules/PostModule.js';
import { AtmosphereModule } from './modules/AtmosphereModule.js';
import { WeatherModule } from './modules/WeatherModule.js';
import { AtlasAmbienceModule } from './modules/AtlasAmbienceModule.js';
import { AtlasMusicModule } from './modules/AtlasMusicModule.js';
import { StreamingModule } from './modules/StreamingModule.js';
import { StructureRenderModule } from './modules/StructureRenderModule.js';
import { CommonsSettlementsModule } from './modules/CommonsSettlementsModule.js';
import { SettlementProxyModule } from './modules/SettlementProxyModule.js';
import { TerrainFarTierModule } from './modules/TerrainFarTierModule.js';
import { RegionalRoutesModule } from './modules/RegionalRoutesModule.js';
import { AtlasRiverModule } from './modules/AtlasRiverModule.js';
import { AtlasWaterAudioModule } from './modules/AtlasWaterAudioModule.js';
import { RaceCircuitModule } from './modules/RaceCircuitModule.js';
import { AtlasCaveModule } from './modules/AtlasCaveModule.js';
import { AtlasAdaptiveQualityModule } from './modules/AtlasAdaptiveQualityModule.js';
import { AtlasShaderWarmModule } from './modules/AtlasShaderWarmModule.js';
import * as THREE from 'three/webgpu';
import { ClusteredLighting } from 'three/addons/lighting/ClusteredLighting.js';
import { SkyShipModule } from './modules/SkyShipModule.js';
import { MerchantHiveModule } from './modules/MerchantHiveModule.js';
import { PersonalPresetModule } from './modules/PersonalPresetModule.js';
import { MerchantTravelModule } from './modules/MerchantTravelModule.js';
import { AtlasCombatHudModule } from './modules/AtlasCombatHudModule.js';
import { AtlasPvpModule } from './modules/AtlasPvpModule.js';
import { AtlasSocialModule } from './modules/AtlasSocialModule.js';
import { BuildingInteriorModule } from './modules/BuildingInteriorModule.js';
import { StreetLampsModule } from './modules/StreetLampsModule.js';
import { AtlasShaderFxModule } from './modules/AtlasShaderFxModule.js';
import { AtlasAuroraModule } from './modules/AtlasAuroraModule.js';
import { AtlasVolumetricLightModule } from './modules/AtlasVolumetricLightModule.js';
import { AtlasAreaLightsModule } from './modules/AtlasAreaLightsModule.js';
import { RainShelterModule } from './modules/RainShelterModule.js';
import { NpcInteractModule } from './modules/NpcInteractModule.js';
import { DecorationModule } from './modules/DecorationModule.js';
import { AtlasAvatarModule } from './modules/AtlasAvatarModule.js';
import { AtlasPassportEditorModule } from './modules/AtlasPassportEditorModule.js';
import { AtlasInventoryModule } from './modules/AtlasInventoryModule.js';
import { AtlasMarketModule } from './modules/AtlasMarketModule.js';
import { AtlasProvenanceModule } from './modules/AtlasProvenanceModule.js';
import { AtlasInputModule } from './modules/AtlasInputModule.js';
import { AtlasWalletModule } from './modules/AtlasWalletModule.js';
import { ForgeCanvasModule } from './modules/ForgeCanvasModule.js';
import { WorldLogicModule } from './modules/WorldLogicModule.js';
import { SocialCompassModule } from './modules/SocialCompassModule.js';
import { OrbitModule } from './modules/OrbitModule.js';
import { AtlasReputationModule } from './modules/AtlasReputationModule.js';
import { AtlasEventPlannerModule } from './modules/AtlasEventPlannerModule.js';
import { AtlasMemoryBookModule } from './modules/AtlasMemoryBookModule.js';
import { AtlasGroupsModule } from './modules/AtlasGroupsModule.js';
import { CreatorExchangeModule } from './modules/CreatorExchangeModule.js';
import { DiscoveryModule } from './modules/DiscoveryModule.js';
import { LiveOpsModule } from './modules/LiveOpsModule.js';
import { OnboardingModule } from './modules/OnboardingModule.js';
import { StructureModule } from './modules/StructureModule.js';
import { CityModule } from './modules/CityModule.js';
import { EnvironmentModule } from './modules/EnvironmentModule.js';
import { LivingEventsModule } from './modules/living-events/LivingEventsModule.js';
import { AtlasMerchantModule } from './modules/AtlasMerchantModule.js';
import { VersonaChatterModule } from './modules/VersonaChatterModule.js';
import { FootprintDecalModule } from './modules/FootprintDecalModule.js';
import { CivicDressingModule } from './modules/CivicDressingModule.js';
import { BattleModule } from './modules/living-events/BattleModule.js';
import { OutpostModule } from './modules/living-events/OutpostModule.js';
import { EventsDebugModule } from './modules/living-events/EventsDebugSection.js';
import { WarAirModule } from './modules/living-events/WarAirModule.js';
import { CargoSkyModule } from './modules/CargoSkyModule.js';
import { BountyHunterModule } from './modules/BountyHunterModule.js';
import { MovementDebugModule } from './modules/MovementDebugSection.js';
import { VersonaDebugModule } from './modules/VersonaDebugModule.js';
import { VersonaLabModule } from './modules/VersonaLabModule.js';
import { AtlasWaterModule } from './modules/AtlasWaterModule.js';
import { AtlasAudioModule } from './modules/AtlasAudioModule.js';
import { AtlasAudioRuntimeModule } from './modules/AtlasAudioRuntimeModule.js';
import { FreeformBuildModule } from './modules/FreeformBuildModule.js';
import { UploadModule } from './modules/UploadModule.js';
import { ForgeStudioModule } from './modules/ForgeStudioModule.js';
import { ArcadeModule } from './modules/ArcadeModule.js';
import { AtlasHudModule } from './modules/AtlasHudModule.js';
import { AtlasPerfHudModule } from './modules/AtlasPerfHudModule.js';
import { AtlasDebugMenuModule } from './modules/AtlasDebugMenuModule.js';
import { AtlasPerfTourModule } from './modules/AtlasPerfTourModule.js';
import { AtlasProbesModule } from './modules/AtlasProbesModule.js';
import { AtlasShaderTourModule } from './modules/AtlasShaderTourModule.js';
import { VersonaUniverseModule } from './modules/VersonaUniverseModule.js';
import { AtlasAdminEditorModule } from './modules/AtlasAdminEditorModule.js';
import { AtlasLoadingModule } from './modules/AtlasLoadingModule.js';
import { AtlasMainMenuModule } from './modules/AtlasMainMenuModule.js';
import { AtlasExchangeOfficeModule } from './modules/AtlasExchangeOfficeModule.js';
import { AtlasChronicleModule } from './modules/AtlasChronicleModule.js';
import { AtlasWorldMapModule } from './modules/AtlasWorldMapModule.js';
import { AtlasLocalMapModule } from './modules/AtlasLocalMapModule.js';
import { AtlasSkyTrafficModule } from './modules/AtlasSkyTrafficModule.js';
import { AtlasNightSkyBodiesModule } from './modules/AtlasNightSkyBodiesModule.js';
import { VersonaTravelModule } from './modules/VersonaTravelModule.js';
import { AtlasDroidModule } from './modules/AtlasDroidModule.js';
import { AtlasVehicleModule } from './modules/AtlasVehicleModule.js';
import { AtlasDroidCommandModule } from './modules/AtlasDroidCommandModule.js';
import { TerrainEditSyncModule } from './modules/TerrainEditSyncModule.js';
import { InteriorMarkerSyncModule } from './modules/InteriorMarkerSyncModule.js';
import { AtlasEditModeModule } from './modules/AtlasEditModeModule.js';
import { AtlasTravelModule } from './modules/AtlasTravelModule.js';
import { SpaceModule } from './modules/SpaceModule.js';
import { AtlasGuildModule } from './modules/AtlasGuildModule.js';
import { WildsModule } from './modules/WildsModule.js';
import { AtlasFaunaModule } from './modules/AtlasFaunaModule.js';
import { AtlasChatModule } from './modules/AtlasChatModule.js';
import { AtlasVoiceModule } from './modules/AtlasVoiceModule.js';
import { AtlasGravesModule } from './modules/AtlasGravesModule.js';
import { AtlasTravelerSelectModule } from './modules/AtlasTravelerSelectModule.js';
import { AtlasIntroVideoModule } from './modules/AtlasIntroVideoModule.js';
import { AtlasCraftingModule } from './modules/AtlasCraftingModule.js';
import { AtlasCraftingGuideModule } from './modules/AtlasCraftingGuideModule.js';
import { AtlasFederalModule } from './modules/AtlasFederalModule.js';
import { AtlasLoadingSlidesModule } from './modules/AtlasLoadingSlidesModule.js';
import { AtlasWelcomeModule } from './modules/AtlasWelcomeModule.js';
import { AtlasProbeLightModule } from './modules/AtlasProbeLightModule.js';
import { AtlasProbeRunnerModule } from './modules/AtlasProbeRunnerModule.js';
import { AtlasHelpModule } from './modules/AtlasHelpModule.js';
import { AtlasGraphicsMenuModule } from './modules/AtlasGraphicsMenuModule.js';
import { XRModule } from 'enginex/xr/index.js';
import { AtlasXRModule } from './modules/AtlasXRModule.js';
import { PersonalBuildGizmoModule } from './modules/PersonalBuildGizmoModule.js';
import { StructurePlacementModule } from './modules/StructurePlacementModule.js';
import { LuaIdeModule } from './modules/LuaIdeModule.js';
import { AtlasWorldsModule } from './modules/AtlasWorldsModule.js';
import { WorldScriptRuntimeModule } from './modules/WorldScriptRuntimeModule.js';
import { NpcUnitModule } from './modules/NpcUnitModule.js';
import { WorldEnvModule } from './modules/WorldEnvModule.js';
import { DeviceModule } from './modules/DeviceModule.js';
import { AtlasMenuBarModule } from './modules/AtlasMenuBarModule.js';
import { AtlasPanelWindowsModule } from './modules/AtlasPanelWindowsModule.js';
import { AtlasOriginRebaseModule } from './modules/AtlasOriginRebaseModule.js';
import { AtlasVRMenuModule } from './modules/AtlasVRMenuModule.js';
import { AtlasVROptimizerModule } from './modules/AtlasVROptimizerModule.js';
import { loadSettings, activePreset, effectivePixelRatio } from './lib/quality_tiers.js';
import { setInstancerWarmHook } from './lib/instancing.js';
import { schedulePipelineWarm, pipelineWarmStats, warmThenShow } from './lib/pipeline_warm.js';
import { hasSchedulerYield } from './lib/yield_main.js';

async function main(): Promise<void> {
  const canvas = document.getElementById('app') as HTMLCanvasElement;
  // Load persisted graphics settings (or auto-detect a starting preset on first run) BEFORE
  // any module builds, so activePreset() is correct from the first frame.
  loadSettings();
  // antialias:false — AA is done in the post chain (G4); MSAA off keeps SSAO valid on WebGPU.
  // Two INDEPENDENT probe flags, because their requirements conflict:
  //   ?perf=1  → measure the REAL (WebGPU) backend with timestamp queries.
  //   ?shots=1 → force a preserveDrawingBuffer WebGL2 context so headless
  //              screenshots capture real pixels. three's WebGPU backend
  //              hardcodes that attribute away and the compositor grabs the
  //              canvas AFTER the buffer swap (classic black-screenshot issue).
  // Combining them would silently measure the fallback renderer, which is what
  // the probe did before 2026-07-25.
  const params = new URLSearchParams(location.search);
  const perfMode = params.has('perf');
  const shotMode = params.has('shots');
  const glContext = shotMode
    ? canvas.getContext('webgl2', { antialias: false, alpha: true, depth: true, stencil: true, preserveDrawingBuffer: true }) as WebGL2RenderingContext
    : undefined;
  // VR-BOOT LAW: Quest-class browsers ship WebGPU now, but three's WebXR
  // manager binds WebGL — on a WebGPU boot the immersive session is GRANTED
  // and then dies on its first frame (the "screen flashes dark and returns"
  // report). Any browser that can run immersive-vr boots the WebGL2 backend
  // from the start; desktops without a headset keep WebGPU.
  let xrCapable = false;
  try {
    xrCapable = !!navigator.xr && await navigator.xr.isSessionSupported('immersive-vr').catch(() => false);
  } catch { /* no WebXR — desktop path */ }
  // THE GPU DESTROY SHIELD (hyperwarp flood, 2026-08-23): three r185 destroys
  // buffers its own render-object churn owns while deferred submits still
  // reference them — no app-level dispose path can catch it. Install before
  // the renderer exists so every buffer/texture the device ever creates is
  // ordered against real queue submits.
  installGpuDestroyShield();
  const app = await App.create({
    canvas,
    antialias: false,
    // The tier's render-scale cap from the FIRST frame (lib/quality_tiers
    // effectivePixelRatio — loadSettings() ran above): the menu and the world
    // streaming behind it no longer render at the full display ratio.
    pixelRatio: effectivePixelRatio(),
    powerPreference: 'high-performance',
    trackTimestamp: perfMode,
    ...(glContext ? { context: glContext, forceWebGL: true } : xrCapable ? { forceWebGL: true } : {}),
  });
  if (xrCapable) console.info('[atlas] VR-capable browser — WebGL2 backend (the WebXR path)');
  // One bad draw must never kill the session: skip + name it (see the guard).
  installDrawGuard(app.renderer.native as unknown as { backend?: unknown });
  // Perf frontier (item 11) measurement lever: Long Animation Frames with
  // script attribution — which scripts and which render phases made the
  // slow frames (the long-task census could not say). window.__loaf().
  {
    const loaf: Array<{ t: number; dur: number; render: number; scripts: Array<{ src: string; dur: number }> }> = [];
    try {
      const PO = (globalThis as unknown as { PerformanceObserver?: new (cb: (l: { getEntries(): unknown[] }) => void) => { observe(o: unknown): void } }).PerformanceObserver;
      if (PO) {
        const po = new PO((list) => {
          for (const e of list.getEntries() as Array<{ startTime: number; duration: number; renderStart?: number; styleAndLayoutStart?: number; scripts?: Array<{ invoker?: string; sourceURL?: string; sourceFunctionName?: string; duration: number }> }>) {
            if (e.duration < 50) continue;
            loaf.push({
              t: Math.round(e.startTime), dur: Math.round(e.duration),
              render: e.renderStart ? Math.round(e.startTime + e.duration - e.renderStart) : 0,
              scripts: (e.scripts ?? []).slice(0, 4).map((sc) => ({ src: `${sc.sourceFunctionName ?? sc.invoker ?? '?'}@${(sc.sourceURL ?? '').split('/').pop() ?? ''}`, dur: Math.round(sc.duration) })),
            });
            if (loaf.length > 600) loaf.shift(); // 600: the perf tour reads a whole leg's worth (a busy settlement leg logs > 30)
          }
        });
        po.observe({ type: 'long-animation-frame', buffered: true });
      }
    } catch { /* browsers without LoAF */ }
    (globalThis as unknown as { __loaf?: (n?: number) => unknown }).__loaf = (n = 30) => loaf.slice(-n);
  }
  (globalThis as unknown as { __impostors?: () => unknown }).__impostors = () => environment.impostorStats();
  // Perf frontier (item 2) measurement lever: how many GPU programs/pipelines
  // the renderer has created so far — the count of material VARIANTS, which
  // the CPU profile showed as the dominant main-thread cost after entry.
  (globalThis as unknown as { __pipelines?: () => unknown }).__pipelines = () => {
    const n = app.renderer.native as unknown as { info?: { programs?: unknown[]; render?: { calls?: number; triangles?: number } }; _pipelines?: { caches?: Map<string, unknown>; programs?: { vertex?: Map<string, unknown>; fragment?: Map<string, unknown>; compute?: Map<string, unknown> } } };
    // three's common Renderer keeps every created pipeline in _pipelines.caches
    // (both backends) and the compiled programs per stage in _pipelines.programs.
    const pipelines = n._pipelines?.caches?.size ?? null;
    const pr = n._pipelines?.programs;
    const programs = pr ? (pr.vertex?.size ?? 0) + (pr.fragment?.size ?? 0) + (pr.compute?.size ?? 0) : (n.info?.programs?.length ?? null);
    return { pipelines, programs, calls: n.info?.render?.calls ?? null, triangles: n.info?.render?.triangles ?? null };
  };
  // Build census (2026-09-20): WHO is building shader graphs, and how often. A steady scene builds NOTHING — a node
  // graph is built once per (material, object kind, light set, fog, MRT …) and cached; every build after the first
  // minute is a render object that was created or INVALIDATED that frame (a cache key that flips, a material made
  // per event). `__buildCensus()` arms it, `__buildCensus('read')` reports builds / frame and the materials behind them.
  {
    interface NodesLike { getForRender(ro: unknown): unknown; get(ro: unknown): { nodeBuilderState?: unknown }; nodeBuilderCache?: Map<unknown, unknown>; __atlasCensus?: boolean }
    const tally = new Map<string, { created: number; built: number; ms: number }>();
    let armedAt = 0, frame0 = 0, created = 0, built = 0, ms = 0;
    (globalThis as unknown as { __buildCensus?: (cmd?: string) => unknown }).__buildCensus = (cmd = 'arm') => {
      const n = app.renderer.native as unknown as { _nodes?: NodesLike; info?: { frame?: number } };
      const nodes = n._nodes;
      if (!nodes || typeof nodes.getForRender !== 'function') return { error: 'renderer._nodes.getForRender is not there (three changed)' };
      if (!nodes.__atlasCensus) {
        nodes.__atlasCensus = true;
        const orig = nodes.getForRender.bind(nodes);
        nodes.getForRender = (ro: unknown) => {
          if (armedAt === 0 || nodes.get(ro).nodeBuilderState !== undefined) return orig(ro);
          const size0 = nodes.nodeBuilderCache?.size ?? 0, t0 = performance.now();
          const out = orig(ro);
          const dt = performance.now() - t0, miss = (nodes.nodeBuilderCache?.size ?? 0) > size0;
          const r = ro as { material?: { name?: string; type?: string; id?: number }; object?: { name?: string; type?: string } };
          const key = `${r.material?.name || r.material?.type || '?'}#${r.material?.id ?? '?'} on ${r.object?.type ?? '?'}:${(r.object?.name || '').replace(/\d+/g, '#').slice(0, 40)}`;
          const row = tally.get(key) ?? { created: 0, built: 0, ms: 0 };
          row.created++; created++;
          if (miss) { row.built++; built++; row.ms += dt; ms += dt; }
          tally.set(key, row);
          return out;
        };
      }
      if (cmd === 'read') {
        const frames = Math.max(1, (n.info?.frame ?? 0) - frame0);
        return {
          seconds: +((performance.now() - armedAt) / 1000).toFixed(1), frames, renderObjectsCreated: created, graphsBuilt: built, buildMs: +ms.toFixed(1),
          builtPerFrame: +(built / frames).toFixed(2), buildMsPerFrame: +(ms / frames).toFixed(2),
          rows: [...tally.entries()].sort((a, b) => b[1].ms - a[1].ms || b[1].created - a[1].created).slice(0, 25).map(([k, v]) => `${String(v.built).padStart(4)} built ${String(v.created).padStart(5)} created ${v.ms.toFixed(0).padStart(6)} ms  ${k}`),
        };
      }
      tally.clear(); created = 0; built = 0; ms = 0; armedAt = performance.now(); frame0 = n.info?.frame ?? 0;
      return { armed: true };
    };
  }
  // Triangle census (2026-09-09, the preset ladder): what a frame's triangles
  // belong to — visible meshes summed as index-triangles × instances (the
  // InstancedMesh count, the instancer pool's draw count), grouped by the
  // object's name up to its first ':' so a family reads as one row.
  (globalThis as unknown as { __triangleCensus?: (top?: number) => unknown }).__triangleCensus = (top = 14) => {
    const rows = new Map<string, { tris: number; draws: number; instances: number }>();
    let total = 0;
    app.world.scene.traverse((o) => {
      if (!o.visible) return;
      const m = o as THREE.Mesh & { isInstancedMesh?: boolean; count?: number };
      if (!m.isMesh && !m.isInstancedMesh) return;
      let vis = true; for (let p: THREE.Object3D | null = o; p; p = p.parent) { if (!p.visible) { vis = false; break; } }
      if (!vis) return;
      const g = m.geometry as THREE.BufferGeometry | undefined;
      if (!g) return;
      const perInst = g.index ? g.index.count / 3 : (g.getAttribute('position')?.count ?? 0) / 3;
      const inst = m.isInstancedMesh ? (m.count ?? 0) : 1;
      const tris = perInst * inst;
      // An instancer batch is named `inst:<pool>:<key>` — keep the POOL (settlements / chunk-foliage /
      // admin-placements …) or every pool collapses into one 'inst' row (perf tour run 1).
      const raw = o.name || o.parent?.name || '(unnamed)';
      const segs = raw.split(':');
      const name = (segs[0] === 'inst' && segs[1] ? `inst:${segs[1]}` : segs[0]).slice(0, 40) || '(unnamed)';
      const r = rows.get(name) ?? { tris: 0, draws: 0, instances: 0 };
      r.tris += tris; r.draws++; r.instances += inst; rows.set(name, r);
      total += tris;
    });
    const list = [...rows.entries()].sort((a, b) => b[1].tris - a[1].tris).slice(0, top).map(([name, r]) => ({ name, tris: Math.round(r.tris), draws: r.draws, instances: r.instances }));
    return { total: Math.round(total), top: list };
  };
  // AO overlay census (2026-09-17): every TRANSPARENT material drawn over a
  // surface must carry the engine's MRT overlay mark (post/mrtOverlay.ts) or
  // it rewrites the ground's normal under it. Lists the unmarked ones by name.
  (globalThis as unknown as { __mrtOverlays?: () => unknown }).__mrtOverlays = () => {
    const marked = new Set<string>(); const unmarked = new Map<string, number>(); let transparent = 0;
    app.world.scene.traverse((o) => {
      const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined; if (!m) return;
      for (const mat of Array.isArray(m) ? m : [m]) {
        if (!mat.transparent) continue; transparent++;
        if (isMrtOverlay(mat)) { marked.add(mat.uuid); continue; }
        const key = `${o.name || o.type}:${mat.name || mat.type}`; unmarked.set(key, (unmarked.get(key) ?? 0) + 1);
      }
    });
    return { transparent, marked: marked.size, unmarked: [...unmarked.entries()].sort((a, b) => b[1] - a[1]).slice(0, 40) };
  };
  // Shader wave stage 2 diag: how many materials carry the engine layers.
  (globalThis as unknown as { __materialFx?: () => unknown }).__materialFx = () => { let cov = null; for (const h of accumulationHandles) { cov = h.coverage.value; break; } return { ...AssetModule.materialFxStats, handles: accumulationHandles.size, coverage: cov, foliage: environment.foliageFxCount() }; };
  // PICTURE lever (the black-frame report, 2026-09-08): render one frame of
  // the whole camera stack + post chain into an offscreen target and read it
  // back through three's async readback — the one pixel measure that works on
  // BOTH backends (a headless WebGPU canvas screenshots white and drawImages
  // transparent). Returns luminance stats over the frame; a counter is not a
  // picture, so probes assert on THIS.
  // `raw` (2026-09-12, the motion-blur-still probe): also return the sampled
  // luminance grid, so two frames can be DIFFED (a smear is area, not a mean).
  (globalThis as unknown as { __framePixels?: (stride?: number, raw?: boolean) => Promise<unknown> }).__framePixels = async (stride = 4, raw = false) => {
    const native = app.renderer.native;
    if ((app.renderer as unknown as { paused?: boolean }).paused) return { paused: true };
    const size = native.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
    const rt = new THREE.RenderTarget(w, h);
    const prev = native.getRenderTarget();
    try {
      native.setRenderTarget(rt);
      app.renderer.renderFrame(app.world.scene);
      native.setRenderTarget(prev);
      const px = await native.readRenderTargetPixelsAsync(rt, 0, 0, w, h) as ArrayLike<number> & { constructor: { name: string } };
      const scale = px instanceof Uint8Array || px instanceof Uint8ClampedArray ? 1 : 255;
      let sum = 0, bright = 0, n = 0;
      const data: number[] = [];
      for (let y = 0; y < h; y += stride) for (let x = 0; x < w; x += stride) {
        const i = (y * w + x) * 4;
        const l = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) * scale;
        sum += l; if (l > 8) bright++; n++;
        if (raw) data.push(Math.round(l));
      }
      const cols = Math.ceil(w / stride);
      return { w, h, mean: sum / n, bright: bright / n, samples: n, type: px.constructor.name, ...(raw ? { stride, cols, data } : {}) };
    } finally { native.setRenderTarget(prev); rt.dispose(); }
  };
  // THE PICTURE (2026-09-18, the shader tour): a real capture through the SAME readback — a WebGPU canvas keeps no
  // drawing buffer (toBlob is blank), so the frame is rendered into a target, read back, downsampled to `maxWidth`
  // (integer stride), flipped for the WebGL2 backend (readPixels is bottom-up) and PNG-encoded off an OffscreenCanvas.
  (globalThis as unknown as { __frameCapture?: (maxWidth?: number) => Promise<unknown> }).__frameCapture = async (maxWidth = 1280) => {
    const native = app.renderer.native;
    if ((app.renderer as unknown as { paused?: boolean }).paused) return { paused: true };
    const size = native.getDrawingBufferSize(new THREE.Vector2());
    const w = Math.max(1, Math.round(size.x)), h = Math.max(1, Math.round(size.y));
    const rt = new THREE.RenderTarget(w, h);
    const prev = native.getRenderTarget();
    try {
      native.setRenderTarget(rt);
      app.renderer.renderFrame(app.world.scene);
      native.setRenderTarget(prev);
      const px = await native.readRenderTargetPixelsAsync(rt, 0, 0, w, h) as ArrayLike<number>;
      const scale = px instanceof Uint8Array || px instanceof Uint8ClampedArray ? 1 : 255;
      const stride = Math.max(1, Math.ceil(w / Math.max(16, maxWidth)));
      const ow = Math.max(1, Math.floor(w / stride)), oh = Math.max(1, Math.floor(h / stride));
      const flip = !(native as unknown as { backend?: { isWebGPUBackend?: boolean } }).backend?.isWebGPUBackend;
      const img = new Uint8ClampedArray(ow * oh * 4);
      let sum = 0, dark = 0, bright = 0;
      for (let oy = 0; oy < oh; oy++) {
        const sy = oy * stride, srcY = flip ? (h - 1 - sy) : sy;
        for (let ox = 0; ox < ow; ox++) {
          const i = (srcY * w + ox * stride) * 4, o = (oy * ow + ox) * 4;
          const r = Math.min(255, px[i] * scale), gg = Math.min(255, px[i + 1] * scale), b = Math.min(255, px[i + 2] * scale);
          img[o] = r; img[o + 1] = gg; img[o + 2] = b; img[o + 3] = 255;
          const l = 0.299 * r + 0.587 * gg + 0.114 * b;
          sum += l; if (l < 8) dark++; if (l > 247) bright++;
        }
      }
      const n = ow * oh;
      const canvas = new OffscreenCanvas(ow, oh);
      const ctx = canvas.getContext('2d');
      if (!ctx) return { w: ow, h: oh, mean: sum / n, dark: dark / n, bright: bright / n, png: '' };
      ctx.putImageData(new ImageData(img, ow, oh), 0, 0);
      const blob = await canvas.convertToBlob({ type: 'image/png' });
      const png = await new Promise<string>((resolve, reject) => { const fr = new FileReader(); fr.onload = () => resolve(String(fr.result).split(',')[1] ?? ''); fr.onerror = () => reject(fr.error); fr.readAsDataURL(blob); });
      return { w: ow, h: oh, srcW: w, srcH: h, mean: sum / n, dark: dark / n, bright: bright / n, png };
    } finally { native.setRenderTarget(prev); rt.dispose(); }
  };
  // Phase 1 (Wasm assessment): the AssemblyScript terrain kernel — bit-identical
  // to shared/terrain.ts, proven by verify:terrain-kernel; JS until it lands.
  void installTerrainKernelFromUrl(undefined, (m) => console.info(`[TerrainKernel] ${m}`));
  // Phase 2: the engine's embedded hydrology/erosion kernel (thermalErode is
  // what personal-world terrain runs at entry; analyzeHydrology for the rest).
  // Same opt-out switch as the terrain kernel; JS oracle until it resolves.
  if (!wasmKernelsOptedOut()) {
    void preloadHydrologyKernel().then((ok) => console.info(`[HydrologyKernel] ${ok ? 'wasm kernel active (thermalErode/analyzeHydrology)' : 'unavailable — TypeScript path'}`));
  }
  // Probe/diag: `__hydrologyKernel().check(n)` runs thermalErode + analyzeHydrology
  // on an n² field of real Commons terrain through the kernel AND the oracle
  // (hook toggled) and reports bit-mismatches + timings.
  // Probe/diag: which texture tiers the asset loader picked (settlement study R1).
  (window as unknown as Record<string, unknown>).__textureTiers = () => assets.textureTierStats();
  // Probe/diag: far-tier massing proxies (settlement study R7).
  (window as unknown as Record<string, unknown>).__proxies = () => proxies.diag();
  // Probe/diag: pack opens / member hits (settlement study R6).
  (window as unknown as Record<string, unknown>).__assetPacks = () => assets.assetPackStats();
  // Probe/diag: warm-then-show + texture warm counters and the yield API (settlement study R4).
  (window as unknown as Record<string, unknown>).__warmStats = () => ({ ...pipelineWarmStats(), schedulerYield: hasSchedulerYield() });
  (window as unknown as Record<string, unknown>).__textureShare = () => assets.textureShareDiag(); // S4 registry
  (window as unknown as Record<string, unknown>).__assetBytes = () => assets.assetBytesStats(); // S8 bytes LRU
  (window as unknown as Record<string, unknown>).__assetSkins = () => assets.assetSkinStats(); // L1/L2 LOD0 skins
  (window as unknown as Record<string, unknown>).__assetCache = () => assets.assetCacheStats(); // L3 persistent cache (OPFS)
  (window as unknown as Record<string, unknown>).__assetCacheClear = () => assets.clearAssetCache();
  // Probe/diag: near-ring foliage loads by LOD (settlement study R2).
  (window as unknown as Record<string, unknown>).__nearLod = () => ({ ...environment.nearLodStats, nearFoliageLod: activePreset().nearFoliageLod, propLod: activePreset().propLod });
  (window as unknown as Record<string, unknown>).__hydrologyKernel = () => ({
    active: hydrologyKernelActive(),
    check: (n = 65, seed = 'commons-genesis') => {
      const k = activeHydrologyKernel();
      if (!k) return { active: false, mismatches: -1, n: 0 };
      const field = new Float32Array(n * n);
      for (let z = 0; z < n; z++) for (let x = 0; x < n; x++) field[z * n + x] = terrainHeight(seed, x * 6 - 200, z * 6 + 340);
      const run = (): { th: Float32Array; an: { filled: Float32Array; receivers: Int32Array; order: Int32Array; accumulation: Float32Array }; ms: number } => {
        const th = new Float32Array(field);
        const t0 = performance.now();
        thermalErode(th, n, { iterations: 16, cellSize: 4 });
        const an = analyzeHydrology(field, n);
        return { th, an, ms: performance.now() - t0 };
      };
      const w = run();
      setHydrologyKernel(null);
      const j = run();
      setHydrologyKernel(k);
      let mismatches = 0;
      const cmp = (a: ArrayLike<number>, b: ArrayLike<number>) => { for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) mismatches++; };
      cmp(w.th, j.th); cmp(w.an.filled, j.an.filled); cmp(w.an.receivers, j.an.receivers); cmp(w.an.order, j.an.order); cmp(w.an.accumulation, j.an.accumulation);
      return { active: true, mismatches, n: n * n * 5, wasmMs: +w.ms.toFixed(2), jsMs: +j.ms.toFixed(2) };
    },
  });
  // Probe/diag: `__terrainKernel().check(n)` samples n points through the
  // kernel AND the JS oracle (hook toggled) and reports bit-mismatches.
  (window as unknown as Record<string, unknown>).__terrainKernel = () => ({
    active: terrainKernelActive(),
    check: (n = 2000, seed = 'commons-genesis') => {
      if (!terrainKernelActive()) return { active: false, mismatches: -1, n: 0 };
      const k = currentTerrainKernel();
      let mismatches = 0; let s = 12345;
      const rnd = () => { s ^= s << 13; s >>>= 0; s ^= s >>> 17; s ^= s << 5; s >>>= 0; return s / 0xffffffff; };
      const pts: number[] = [];
      for (let i = 0; i < n; i++) pts.push((rnd() - 0.5) * 400000, (rnd() - 0.5) * 400000);
      const a: number[] = [];
      for (let i = 0; i < n; i++) a.push(terrainHeight(seed, pts[i * 2], pts[i * 2 + 1]));
      setTerrainKernel(null);
      try { for (let i = 0; i < n; i++) if (!Object.is(a[i], terrainHeight(seed, pts[i * 2], pts[i * 2 + 1]))) mismatches++; }
      finally { setTerrainKernel(k); }
      return { active: true, mismatches, n };
    },
  });

  // Every NEW instanced batch is a NEW pipeline compiled synchronously at first
  // view — warm it the moment any spawner creates one (single chokepoint hook).
  // R4: a newborn instancer batch is hidden until its pipelines + buffers are
  // warm (compileAsync off the critical path), then revealed — the batch used
  // to draw (and link/upload) the frame it was born, 600 ms before its warm.
  setInstancerWarmHook((mesh) => { if (mesh) void warmThenShow(app, mesh); else schedulePipelineWarm(app); });

  // Registration order = init order. Identity is referenced by Network (it
  // routes the resolved identity into the passport kiosk). World/Avatar render;
  // Input drives intent; Hud reflects state.
  const identity = new AtlasIdentityModule();
  const assets = new AssetModule();
  const physics = new PhysicsModule();
  const culling = new RenderCullingModule(); // G2: RenderSub LOD-selection + culling
  const lighting = new LightingModule(assets); // G3: sun + IBL + CSM shadows + ACES
  const post = new PostModule(); // G4: AA + bloom + SSAO + DoF, quality-tiered
  const freeform = new FreeformBuildModule(assets, physics);
  // Forward+ clustered lighting — OFF BY DESIGN, opt-in only (reviewed
  // 2026-09-15). Two findings, both from source:
  //
  //  1. It never actually installed. The guard read `renderer.native.isWebGPU`,
  //     and three's WebGPURenderer has no such property (it exposes
  //     `isWebGPURenderer`, and that is true even on its WebGL2 fallback).
  //     `undefined` is falsy, so this line has been a no-op since it landed.
  //  2. Turning it on would not help. three's ClusteredLightsNode clusters
  //     ONLY non-shadow-casting POINT lights —
  //     `if (light.isPointLight === true && light.castShadow !== true)`
  //     (examples/jsm/tsl/lighting/ClusteredLightsNode.js:218). At high and
  //     ultra `lampCookies` makes the street lamps cookie SPOTLIGHTS, which it
  //     skips entirely, and the pool is capped at 10 either way
  //     (StreetLampsModule.MAX_LIGHTS). Everything else — cave glow, hive
  //     plaza, personal-preset lanterns — is one or two lights at a time.
  //     Clustering pays for its per-frame froxel build at dozens to hundreds
  //     of point lights, not at ten; below that it is pure overhead.
  //
  // So the switch is inverted rather than deleted: the path is kept, the
  // backend test is correct, and it turns on for `localStorage
  // 'atlas:clustered' = 'on'`. Worth revisiting if a world ever carries many
  // player-placed light DEVICES (DeviceModule), which are point lights and
  // unbounded — that is the case where it would start to earn its keep.
  try {
    if (localStorage.getItem('atlas:clustered') === 'on' && app.renderer.isWebGPU) {
      (app.renderer.native as unknown as { lighting?: unknown }).lighting = new ClusteredLighting(256, 32, 24, 32);
      console.info('[Lighting] Forward+ clustered lighting ENABLED (atlas:clustered=on)');
    }
  } catch { /* an old three or no localStorage: the default lighting stays */ }
  const environment = new EnvironmentModule(assets, culling, physics);
  const warAir = new WarAirModule(assets, environment); // persistent war wings (rows drive it)
  const versonaDebug = new VersonaDebugModule(environment); // admin-only: Alt+Click a versona → live inspector + edit tools (also the Versona Universe's dossier)
  const sky = new SkyModule();
  const water = new AtlasWaterModule();
  const settlements = new CommonsSettlementsModule(assets, physics);
  // Far tier (settlement study R7): massing skylines for settlements beyond the full-set radius.
  const proxies = new SettlementProxyModule(settlements, assets);
  const npcTalk = new NpcInteractModule();
  const adminEditor = new AtlasAdminEditorModule(assets);
  const audioMod = new AtlasAudioModule(); // zones + the device speakers' positional clips
  const devices = new DeviceModule(physics, audioMod); // Edit Mode devices: lights/FX/signs/doors/triggers/teleporters/zones/water/cinematics/paths/timers + the signal bus
  const npcs = new NpcUnitModule(assets, devices); // Edit Mode NPC creator: bodies/senses/talk + the npc.* Lua table (paths + obstacles from devices)
  // ── PHASE 1 — the menu shell. ONLY what the homepage needs: identity +
  // the SpacetimeDB connection (account-ready unlocks the menu), XR entry,
  // the traveler (avatar) select screen, and the menu itself. NOTHING
  // world-shaped inits here — no terrain/sky/water/settlements/physics —
  // so the homepage is instant and quiet on every device (the Quest hang
  // lived in the heavy boot), and no world ever loads behind the menu.
  app
    .use(identity)
    .use(new AtlasNetworkModule(identity))
    // The shared audio runtime's per-frame half (listener pose, the room, the tier, the world epoch). PHASE 1: the
    // menu has a mix and a master volume too. The runtime itself is a singleton that needs no module to play.
    .use(new AtlasAudioRuntimeModule());

  // ── PHASE 2 — the world. Same modules, same order as the classic boot,
  // but collected and deferred: they init (App.useLate) only once the
  // player PICKS a destination on the menu (atlas:world-picked — while
  // they choose their traveler, the world boots in parallel), and entry
  // itself additionally awaits the boot (AtlasNetworkModule).
  const phase2: IModule[] = [];
  const late = { use(m: IModule) { phase2.push(m); return late; } };
  late
    .use(new AtlasWorldModule())
    .use(assets)
    .use(physics)
    .use(culling)
    .use(lighting)
    // Phase C, Task 2: register CloudModule BEFORE SkyModule so that when
    // SkyModule.init() runs it can call app.getModule('Clouds').renderer.buildCompositeNode()
    // to wrap the cloud node over the background. The engine auto-sun-pull targets a module
    // named 'Sky'; atlas wraps the engine sky privately as 'AtlasSky', so drive the cloud
    // sun explicitly from the exported uSunDir uniform (AtlasSkyUniforms updates it each
    // frame: world-space unit vec FROM origin TO sun).
    .use(new CloudModule({
      qualityPreset: 'medium',
      // Wind ~2 m/s (twice reduced from 48 then 12 m/s): drift must sit WELL
      // BELOW walk speed (~5-7 m/s) or a downwind cloud retreats faster than
      // the player can chase it and "you can never walk toward a cloud" stays
      // literally true even with perfect world-anchoring. ~2 m/s keeps the
      // sky alive over minutes while a walking player visibly gains on it.
      // Slab 3800-9600 m (user-tuned ×2 from 1900-4800): nearby masses sit
      // ~52° up instead of ~37° — the deck reads as properly high overhead.
      cloud: { bottomAltitude: 3800, topAltitude: 9600, windX: 2, windZ: 0.7, windTimeScale: 1, coverage: 0.4 },
      // 2026-08-19: clouds as REAL 3D MESHES in the world (user directive —
      // the screen-space slab reads as "painted on / follows the camera" no
      // matter the anchoring, because walk-speed parallax on a km-high deck
      // is below perception). Pooled sphere volumes at 600-1800 m raymarch
      // the shared noise per fragment: true parallax, true occlusion, true
      // frustum culling. When configured, PostModule routes the tier's
      // cloudsEnabled flag to the field and skips the post-pass composite.
      // fieldRadius must fit inside the avatar camera's 4000 m far plane
      // (AtlasAvatarModule) — the first cut used 12 km and everything beyond
      // ~4 km was silently frustum-clipped (hard pop at the far plane, ~30 of
      // 44 pool meshes spent on never-visible cells). 4200 puts the distance
      // fade (starts at 0.72·radius ≈ 3 km) fully BEFORE the clip.
      // baseAltitude 900: the shared condensation level every flat cloud
      // base sits at (real cumulus bases align; tops vary by type) — high
      // enough to clear the commons' high-relief settlement hills.
      // Pool 72: three étages share it (fair-weather split ~43 low cumulus /
      // 11 altocumulus / 18 cirrus). WeatherModule drives the formation live
      // (formationForWeather — frontal-sequence doctrine).
      meshField: {
        fieldRadius: 4200, cellSize: 700, maxClouds: 72,
        baseAltitude: 900, baseJitter: 40, radiusMin: 220, radiusMax: 560,
      },
      // Low scattered puffs (~800-1500 m): the high deck barely parallaxes at
      // walk speed (atan(50/3800) ≈ 0.75°) and perceptually "follows" the
      // player; low puffs swing ~4° per 50 m walked, making the sky's
      // world-anchoring visible. Composited only when the tier's lowClouds
      // flag is on (PostModule); building it here costs nothing when off.
      // (Legacy path — used when the mesh field is disabled/absent.)
      lowLayer: { cloud: { bottomAltitude: 800, topAltitude: 1500, coverage: 0.22 }, qualityPreset: 'low' },
      sunDirectionProvider: () => { const v = uSunDir.value as { x: number; y: number; z: number }; return { x: v.x, y: v.y, z: v.z }; },
    }))
    .use(sky)
    .use(post)
    .use(new AtmosphereModule()) // G4: time-of-day fog / aerial perspective
    .use(new WeatherModule()) // G4: rain/snow VFX + wet surfaces + foliage wind
    .use(new AtlasAmbienceModule()) // ambient beds + UI/movement SFX (Mirelo clips)
    .use(new AtlasMusicModule()) // Minecraft-style occasional music + combat overrides (Sonilo)
    .use(new StreamingModule(assets)) // G5: AOI-region asset streaming + prefetch + LRU eviction
    .use(new StructureRenderModule(assets, physics, culling))
    .use(settlements)
    .use(proxies) // procedural Commons settlements (instanced + streamed)
    .use(new TerrainFarTierModule(environment)) // the ground to the horizon: four nested coarse grids (HLOD-1/HLOD-0 for terrain), textured like the chunks
    .use(new RegionalRoutesModule(physics)) // route bridges over water (Unbuilt Town wave)
    .use(new AtlasRiverModule()) // river ribbons + waterfall sheets in the baked channels (terrain wave 2026-09-08)
    .use(new AtlasWaterAudioModule()) // the SOUND of rivers, waterfalls, lakes and the sea (2026-09-20) — after the river module it reads
    .use(new RaceCircuitModule(physics)) // the Grand Circuit: ribbon, barriers, sector gantries, the lap HUD (capital wave phase 5)
    .use(new SkyShipModule(assets, physics, environment, npcTalk)) // the boardable mega-ship over every city
    .use(new MerchantHiveModule(assets, physics)) // the Merchant Hive world (flat ground + shop city)
    .use(new MerchantTravelModule()) // F9 hive teleport + the Return chip
    .use(new AtlasCombatHudModule(assets)) // ground combat: vitals, action bar, target frame
    .use(new AtlasPvpModule()) // ground PvP: duels, the Relay Skirmish arena, conflict sectors, tells and result feedback (2026-09-18)
    .use(new AtlasSocialModule()) // player-to-player: parties, pings, friends, private whispers, trades, safety (P; 2026-09-18)
    .use(new BuildingInteriorModule(physics, assets)) // walkable building interiors + versona-home furnishing
    .use(new StreetLampsModule()) // settlement street lights come ON at night (pooled PointLights + glows)
    .use(new AtlasShaderFxModule()) // shader wave stage 3 drivers: lit windows by night, soft-particle far plane, __stage3()
    .use(new AtlasVolumetricLightModule()) // shader additions 2026-09-15: the shadowed sun/lamp light volume around the player, __lightVolume()
    .use(new AtlasAreaLightsModule()) // shader additions 2026-09-15: LTC rect area lights on lit windows at night, __areaLights()
    .use(new RainShelterModule()) // render wave 1 (2026-09-17): the top-down roof map — rain stops under cover, the ground under it stays dry, __rainShelter()
    .use(new AtlasAuroraModule()) // stage 4: aurora curtains on some Commons nights
    .use(npcTalk) // T near an NPC: salience dialogue + LLM-last-resort free text
    .use(new DecorationModule(assets))
    .use(new AtlasAvatarModule(physics))
    .use(new AtlasPassportEditorModule())
    .use(new AtlasInventoryModule())
    .use(new AtlasMarketModule())
    .use(new AtlasProvenanceModule())
    .use(new AtlasInputModule())
    .use(new AtlasWalletModule())
    .use(new ForgeCanvasModule())
    .use(new WorldLogicModule())
    .use(new SocialCompassModule())
    .use(new OrbitModule())
    .use(new AtlasReputationModule())
    .use(new AtlasEventPlannerModule())
    .use(new AtlasMemoryBookModule())
    .use(new AtlasGroupsModule())
    .use(new CreatorExchangeModule())
    .use(new DiscoveryModule())
    .use(new LiveOpsModule())
    .use(new OnboardingModule())
    .use(new StructureModule())
    .use(new CityModule())
    .use(environment) // physics → NEAR-ring terrain trimesh colliders
    .use(new AtlasCaveModule(assets, physics)) // explorable cave interiors in the mountain belts (terrain wave 2026-09-08)
    .use(new AtlasAdaptiveQualityModule(post)) // frame-time governor on the FSR render scale (perf frontier 2026-09-08)
    .use(new AtlasShaderWarmModule()) // whole-scene compileAsync sweeps after entry (perf frontier item 2)
    .use(new PersonalPresetModule(physics, assets)) // styled personal worlds: mansion / penthouse / cottage (photoreal PBR)
    .use(new LivingEventsModule(environment)) // living events: feasts/festivals/rites/hustings/speeches/street scenes staged at their venues
    .use(new AtlasMerchantModule(environment)) // merchant slice 2026-09-15: merchants at their counter face the customer; __merchants()
    .use(new VersonaChatterModule(environment)) // overheard talk: bubbles over chatting pairs within earshot (eavesdrop wave 2026-09-10)
    .use(new FootprintDecalModule(environment)) // footprints behind the player + walking versonas on every outdoor ground; snow prints when it snows
    .use(new CivicDressingModule(environment)) // civic residue + forecast: notice board (E), war banners, bunting, memorials, treaty stones, occupation colors, the crier's bell
    .use(new BattleModule(environment, assets)) // wars as marched battles: bodies, weapons, bolts, explosions, health bars
    .use(new OutpostModule(environment, assets)) // each side's field base: fence walls, aid station, bay, banner, turrets (war campaign 2026-09-16)
    // (warAir is constructed above so the travel module can be attached below)
    .use(warAir) // the persistent piloted air war: named pilots hold the sky for the whole WAR, shootable from the player's ship
    .use(new CargoSkyModule(assets)) // the sky trade lane: escorted freighters between settlements, pirateable + salvageable
    .use(new BountyHunterModule(assets)) // hired stalkers: grudge-funded hunters that shadow the mark and strike at weakness
    .use(water) // Commons sea: shoaling waves + surf foam over stampedHeight beaches
    .use(audioMod)
    .use(freeform)
    .use(new PersonalBuildGizmoModule(freeform)) // B: Build Mode v2 (undo stack, outliner, inspector, budgets)
    .use(new StructurePlacementModule()) // interactive deed placement (ghost + validity preview)
    .use(versonaDebug)
    .use(new VersonaLabModule(environment)) // F4: group observatory — live roster/feed, feel controls, scripted scenarios
    .use(new LuaIdeModule(freeform, npcs)) // I: Lua IDE v3 (world/NPC scripts + contracts, copilot w/ test reports, object binding) — Edit Mode surface
    .use(new AtlasWorldsModule()) // M: create/share/enter your worlds (spawnWorld/setWorldRole/visibility)
    .use(new WorldEnvModule()) // per-world ENVIRONMENT row → lighting/fog/ambience multipliers (Edit Mode panel)
    .use(devices) // world_device rows → behaviour on every visitor's client
    .use(npcs) // scripted NPC units (npc_unit rows) — host for the runtime's npc.* slots
    .use(new WorldScriptRuntimeModule(assets, freeform, npcs, devices)) // per-world CLIENT Lua (engine sandbox; world.* bridge v3 + npc.* + devices)
    .use(new UploadModule()) // G6: creator UGC upload panel
    .use(new ForgeStudioModule())
    .use(new ArcadeModule())
    .use(new AtlasHudModule())
    .use(new AtlasHelpModule()) // in-game searchable help/manual (H or the ? button — all players)
    .use(new AtlasPerfHudModule()) // G2: FPS + live draw-call budget overlay (F3)
    .use(adminEditor) // dev-gated admin world-building editor (Phase 2: fly + palette + place)
    .use(new AtlasDebugMenuModule()) // dev-gated admin debug panel (backtick toggle)
    .use(new AtlasPerfTourModule()) // the live perf tour: four legs on every tier → perf_run/perf_leg/perf_sample (its card lives in the Probes window)
    .use(new AtlasShaderTourModule()) // the shader tour: every shader alone / all / the interference combos — metrics + a picture per leg into atlas/debug/shader-tour/
    .use(new AtlasProbesModule()) // the Probes window (debug panel → Probes…): the perf tour + shader tour cards + every diagnostic probe, run and recorded
    .use(new EventsDebugModule()) // Debug menu → Events & battles: teleport to any live event / battle / war clash
    .use(new MovementDebugModule()) // Debug menu → Movement: ack lag, reconcile decisions, unstick/nudge counters, capsule step cap
    .use(new VersonaUniverseModule(assets, versonaDebug)) // Debug menu → Versonas: every Commons versona — body, stats, activity, relationships, location, memory (admin)
    .use(new AtlasGraphicsMenuModule()); // Esc graphics/options menu (presets + per-feature + FPS)

  // ── WebXR (Meta Quest 3 target) ────────────────────────────────────────────
  // Quest's browser DOES ship WebGPU now — the VR-BOOT LAW above forces the
  // WebGL2 backend whenever immersive-vr is supported, because three's WebXR
  // manager is a WebGL binding. Foveation/scale/local-floor are set BEFORE
  // any session per the engine contract.
  const xr = new XRModule({
    defaultMode: 'immersive-vr',
    referenceSpaceType: 'local-floor',
    requiredFeatures: ['local-floor'],
    optionalFeatures: ['hand-tracking'],
    // Quest 3 tuning (Meta WebXR perf guidance): FFR 0.7 — peripheral
    // resolution drop is near-imperceptible and buys real GPU headroom on
    // an open-world scene; framebuffer 0.9 ≈ −19% fragments for a slight
    // sharpness cost; 72 Hz target = the largest per-frame budget the
    // device offers (13.9 ms vs 11.1 at 90) — atlas is far too heavy to
    // chase 120. All three apply BEFORE setSession per the engine contract.
    // Foveation 1.0 (max): tile-GPU fragment savings at the periphery are
    // near-imperceptible (Meta FFR guidance) and atlas is fragment-bound.
    // 0.75 ≈ −31% fragments vs 0.9 — the single biggest GPU lever for a
    // heavy open world on a mobile GPU; sharpness cost accepted ("lag is
    // still bad" field report 2026-08-16). Session-scoped: desktop unaffected.
    // On-device A/B without code pushes (eye-misalignment bisecting):
    // ?xrscale=1 forces scale 1.0, ?xrfov=0 disables foveation.
    foveation: Number(new URLSearchParams(location.search).get('xrfov') ?? 1.0),
    framebufferScaleFactor: Number(new URLSearchParams(location.search).get('xrscale') ?? 0.75),
    // Force the classic XRWebGLLayer path (2026-08-15): stereo has NEVER
    // been verified good on-device since the r185 upgrade, and the field
    // symptom (overlapping eyes offset upper-left, head-lagged) is a layer-
    // composition artifact on the XRProjectionLayer path. The base-layer
    // path is the one the vr-sim emulated Quest 3 probe validates; the
    // [XRView] beacon line proves which path ran (base=XRWebGLLayer).
    useLayersApi: false,
    targetFrameRate: 72,
    allowWebGpuXr: false,
  });
  app.use(xr).use(new AtlasXRModule(xr)).use(new AtlasVRMenuModule(xr));
  // VR-only render fixes + Quest-class quality profile (XR-clear, WebGL2 depth
  // range, shadows/foliage/fog clamp). Inert outside an immersive session;
  // snapshots + restores everything it touches, so the desktop render path and
  // the player's saved graphics settings are provably unaffected.
  app.use(new AtlasVROptimizerModule());
  // SWG-style click toolbar — every panel without touching the keyboard.
  late.use(new AtlasMenuBarModule());
  // Boot loading screen: gates exploration until terrain/water/sky/foliage/
  // structures report ready (movement intent is ignored while it shows).
  late.use(new AtlasLoadingModule({ environment, sky, water, settlements, adminEditor }));
  // The Exchange Office (U near a town): the goods-only SOV↔commodity bridge.
  late.use(new AtlasExchangeOfficeModule());
  // The Town Chronicle (J near a town): annals + sifted stories + residency.
  late.use(new AtlasChronicleModule());
  late.use(new AtlasWorldMapModule());
  // The SWG-style LOCAL map (M): surroundings, entities, structures.
  late.use(new AtlasLocalMapModule());
  // Ambient ship flybys over the Commons (living-world sky traffic).
  late.use(new AtlasSkyTrafficModule(assets));
  late.use(new AtlasNightSkyBodiesModule());
  const travel = new VersonaTravelModule(assets);
  late.use(travel); // travel wave: Versonas crossing the Commons by craft (ships aloft / hovercraft on land)
  warAir.attachTravel(travel); // war escorts formate on the real marching craft
  late.use(new AtlasDroidModule());
  late.use(new AtlasDroidCommandModule(assets));
  late.use(new TerrainEditSyncModule());
  late.use(new InteriorMarkerSyncModule(freeform, adminEditor));
  late.use(new AtlasEditModeModule(freeform, adminEditor, assets, npcs, devices)); // F2: ONE Edit Mode (replaces B/G/F7 windows)
  late.use(new AtlasVehicleModule(assets));
  late.use(new AtlasTravelModule());
  late.use(new SpaceModule());
  late.use(new AtlasGuildModule());
  late.use(new WildsModule());
  late.use(new AtlasFaunaModule()); // the birds (perch on trees / roofs / parked ships, forage, drink, flee, migrate) + the woods' insect swarm
  late.use(new AtlasChatModule());
  late.use(new AtlasVoiceModule());
  late.use(new AtlasGravesModule());
  app.use(new AtlasTravelerSelectModule());
  late.use(new AtlasCraftingModule());
  // The Crafting Guide window (F10 / Recipes) — every recipe against your holdings.
  late.use(new AtlasCraftingGuideModule());
  late.use(new AtlasFederalModule()); // society wave: the federal docket (the Assembly window)
  // The pre-alpha welcome window, every time a player joins (once per page load).
  late.use(new AtlasWelcomeModule());
  // Headless-probe inspection lighting (localStorage 'atlas:probe' = '1' only).
  late.use(new AtlasProbeLightModule());
  // Agent-runnable probe suites (`?probe=all` URL param only — inert otherwise).
  late.use(new AtlasProbeRunnerModule());
  // The boot loading screen's painting crossfade (index.html #load-slides).
  app.use(new AtlasLoadingSlidesModule());
  // Main menu: covers boot until the player picks Enter/Create (see the module).
  app.use(new AtlasMainMenuModule());
  // The intro video over the boot loading screen (after the traveler's BEGIN).
  app.use(new AtlasIntroVideoModule());
  // Windowize the classic index.html side docks (Passport/Compass/Orbits/Forge/Logic).
  late.use(new AtlasPanelWindowsModule());
  // FP64 origin rebase: cancels 1,000-km translations in f64 before f32 upload.
  late.use(new AtlasOriginRebaseModule());

  // The static #load-screen paints from first load (its module owns it only
  // once the world boots) — phase 1 hides it so the HOMEPAGE owns the
  // viewport, phase 2 restores it right as the world build begins.
  const loadScreen = document.getElementById('load-screen');
  if (loadScreen) loadScreen.style.display = 'none';

  // Deferred world boot: idempotent, first triggered when a destination is
  // picked (dock card / commons / personal) so the heavy init overlaps the
  // traveler-select screen; AtlasNetworkModule awaits it before entering.
  let worldBoot: Promise<void> | null = null;
  atlas.ensureWorldBoot = () => {
    worldBoot ??= (async () => {
      if (loadScreen) loadScreen.style.display = '';
      // One throwing module must not abort the rest of the boot AND latch the
      // promise rejected (the player sat on a permanent loading screen with
      // only an unhandled rejection in the console).
      for (const m of phase2) {
        try { await app.useLate(m); }
        catch (err) { console.error(`[atlas] world module ${(m as { name?: string }).name ?? '?'} failed to init`, err); bus.emit('atlas:status', { text: `${(m as { name?: string }).name ?? 'a world module'} failed to start` }); }
      }
      bus.emit('atlas:status', { text: 'world systems ready' });
    })();
    return worldBoot;
  };
  bus.on('atlas:world-picked', () => { void atlas.ensureWorldBoot?.(); });

  await app.start();

  // Dev console handle — start.sh documents `atlas.conn.reducers.…` for
  // bootstrap/admin calls (createAccount, debugSetWorldSize, …) and the
  // headless smoke harness drives the app through it. `app` rides along so
  // scripts can inspect the scene.
  (window as unknown as { atlas: typeof atlas & { app: typeof app; setDebugTod: typeof setDebugTod } }).atlas =
    Object.assign(atlas, { app, setDebugTod });

  window.addEventListener('resize', () => {
    app.renderer.resize(window.innerWidth, window.innerHeight);
  });

  window.addEventListener('beforeunload', () => app.dispose());
}

main().catch((err) => {
  console.error('[atlas] fatal:', err);
  const pre = document.createElement('pre');
  pre.textContent = (err as Error).stack ?? String(err);
  pre.style.cssText =
    'position:fixed;inset:0;margin:0;padding:16px;color:#ff8a8a;' +
    'background:#1a0000;white-space:pre-wrap;font:13px/1.4 monospace;z-index:99';
  document.body.appendChild(pre);
});
